import { cloneDeep } from "lodash";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { treeActions } from "../../../redux/slices/treeSlice";
import { getUpdatedData as inboundUpdatedData } from "./NewRecord";
import { getUpdatedData as outboundUpdatedData } from "./NewRecordOutbound"
import { RECORD_TYPE, DATA_TYPE } from "../../../constants/datatypes";
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import { genKey } from "../../../utils/utilFunctions";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";


const NewFieldGroup = ({ toggle, openModal }) => {
  const { t } = useTranslation();
  const parentScreen = useContext(ParentScreenCtx);
  const { rightTree } = useSelector((state) => state.treeData[parentScreen]);
  const { currentScreen } = useSelector((state) => state.treeData)
  const currentTab = rightTree.currentTab;
  const selectedNode = rightTree.tree[currentTab].selectedNode;
  const templateHeader = rightTree.tree.templateHeader;
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const content = t("BOX_LIKE_TO_PLACE_FLD_INSIDE_CURR_FLDGRP_OR_DIRECTLY_AFTER")
    .split(`\\n`)
    .join("<br/>");
  const contentRef = useRef();
  
  const handleNewFieldGroup = (isInside) => {
    let nestedLevels = selectedNode?.key?.split("-");
    nestedLevels = nestedLevels.splice(1);
    const translatedData = cloneDeep(rightTree.tree[currentTab].translatedData);

    // Function For Getting New Record Title and Key
    const getNewFieldGroupData = (data, key) => {
      //If it is empty so it will make first node with 'ib-0' key
      if (!data.length)
        return {
          key: `${key}-0`,
          title: `${t("UNNAMED")}1`,
          insertingPosition: 1,
          LENGTH: 0
        };

      let latestNewNodeNum = 0;
      let insertingPosition = 1;
      data.forEach((ele, idx) => {
        // Checking if Unnamed is present already for creating next unnamed Field Group
        const fieldTitle = ele?.TYPE;
        if (fieldTitle && fieldTitle.startsWith(`${t("UNNAMED")}`)) {
          const latestUnnamedFieldGroup = Number(
            fieldTitle.slice(t("UNNAMED").length)
          );
          if (
            !isNaN(latestUnnamedFieldGroup) &&
            ["FIELD", "FIELD_GROUP"].includes(ele?.ICON_DISPLAY)
          ) {
            latestNewNodeNum =
              latestUnnamedFieldGroup > latestNewNodeNum
                ? latestUnnamedFieldGroup
                : latestNewNodeNum;
          }
        }
        if (ele.key === selectedNode.key) {
          insertingPosition = idx + 2;
        }
      });

      const lastRecordKey = data[data.length - 1]?.key?.split("-");
      lastRecordKey[lastRecordKey.length - 1] =
        +lastRecordKey[lastRecordKey.length - 1] + 1;
      const newRecordKey = lastRecordKey.join("-");
      return {
        key: newRecordKey,
        title: `${t("UNNAMED")}${latestNewNodeNum ? +latestNewNodeNum + 1 : 1}`,
        insertingPosition,
      };
    };

    // Inserting New Node Object in Tree Data (Recursive Function);
    function insertNodeInNestedTree(translatedData, nestedLevels, isInside) {
      const level = isInside ? 0 : 1;
      if (nestedLevels.length === level) {
        const { children, key } = translatedData;
        const contextKey = currentScreen === 'inbound' ? 'ib' : {header: "obh-0", body: "ob-0", trailer: "obt-0"}[currentTab]
        const newFieldGroupDetails = children
          ? getNewFieldGroupData(children, key)
          : getNewFieldGroupData(translatedData, contextKey);

        const newNode = {
          nodeId: genKey(newFieldGroupDetails.title),
          alternateNodeId: genKey(newFieldGroupDetails.title),
          key: newFieldGroupDetails.key,
          title: currentScreen === 'inbound' ? `${newFieldGroupDetails.title}` : `${t('FIELD_GROUP')}: ${newFieldGroupDetails.title}`,
          ICON_DISPLAY: "FIELD_GROUP",
          type: "FIELD_GROUP",
          children: [],
          TYPE: parentScreen === "inbound" ? newFieldGroupDetails.title : undefined,
          // LENGTH: 0,
          COMPONENT_TYPE : "COMPONENT",
          EDIT_RULE: [
            {
              TYPE: "RECORD_RULE",
            }
          ],
          fieldNumber: `${templateHeader?.FILETYPE === "EDI" ? 
                        ( (selectedNode?.ICON_DISPLAY?.includes("FIELD_GROUP") && 
                        isInside) ? newFieldGroupDetails.insertingPosition :newFieldGroupDetails.insertingPosition - 1) 
                        : newFieldGroupDetails.insertingPosition}`,
        };
        if(currentScreen === 'outbound'){
          newNode.DESCRIPTION= newFieldGroupDetails.title
          newNode.FIELD_GROUP = "TRUE";
          // newNode.ELEMENT_ID = ""
          newNode.LOOP = "SINGLE";
          newNode.EMPTY_ACTION = "EMPTY";
        } 
        if (children) {
          if (isInside) {
            translatedData.children.splice(0, 0, newNode);
            let newKey = selectedNode.key.split("-");
            newKey.push("0");
            newKey = newKey.join("-");
            dispatch(
              treeActions.setRightTreeSelectedNode({
                screen: parentScreen,
                type: currentTab,
                data: { ...newNode, key: newKey },
              })
            );
          } else {
            translatedData.children.splice(
              newFieldGroupDetails.insertingPosition - 1,
              0,
              newNode
            );
            let newKey = selectedNode.key.split("-");
            newKey[newKey.length - 1] = Number(newKey[newKey.length - 1]) + 1;
            newKey = newKey.join("-");
            dispatch(
              treeActions.setRightTreeSelectedNode({
                screen: parentScreen,
                type: currentTab,
                data: { ...newNode, key: newKey },
              })
            );
          }
        }
        return;
      }

      const isArray = Array.isArray(translatedData);
      if (isArray) {
        const nestedData = translatedData[[nestedLevels[0]]];
        const newNestedLevels = nestedLevels.splice(1);
        insertNodeInNestedTree(nestedData, newNestedLevels, isInside);
      } else {
        // const newNestedLevels = nestedLevels.splice(1);
        insertNodeInNestedTree(translatedData.children, nestedLevels, isInside);
      }
    }

    insertNodeInNestedTree(translatedData, nestedLevels, isInside);
    const getData = currentScreen === 'inbound' ? inboundUpdatedData : outboundUpdatedData
    const updatedData = getData(
      translatedData,
      templateHeader?.FILETYPE,
      null,
      currentScreen === 'workflow',
      currentTab
    );
    dispatch(
      treeActions.setNewRightTreeTranslatedData({
        screen: parentScreen,
        type: currentTab,
        data: updatedData,
      })
    );
    toggle(null);
    setShowModal(false);
 };
 setTimeout(() => {
  if (contentRef.current?.innerHTML === "") contentRef.current.innerHTML = content
}, 0)

  // Checking Placment (Inside or After it) for Field Group

  const handleFieldGroupPlacement = (key)=>{
    let nestedLevels = key?.split("-") || [];
    nestedLevels.pop()
    nestedLevels = nestedLevels.splice(1);
    // console.log(nestedLevels);
    return nestedParentChecking(rightTree.tree[currentTab].translatedData,nestedLevels)

    function nestedParentChecking(translatedData, nestedLevels) {
      // Checking if parent is FIELD_GROUP or not;
      if (
        ["FIELD_GROUP", "REPEATING_FIELD_GROUP"].includes(
          translatedData?.ICON_DISPLAY
        ) &&
        templateHeader.FILETYPE !== "DELIM_FLD_ID"
      ) {
        return false;
      }
      // If there is not any parent FIELD_GROUP then return true for placing FIELD GROUP inside
      if (nestedLevels?.length === 0) {
        return true;
      }

      const isArray = Array.isArray(translatedData);
      if (isArray) {
        const nestedData = translatedData[[nestedLevels[0]]];
        const newNestedLevels = nestedLevels.splice(1);
        return nestedParentChecking(nestedData, newNestedLevels);
      } else {
        return nestedParentChecking(translatedData.children, nestedLevels);
      }
    }
  };

  useEffect(() => {
    if (!openModal) return;
    if (parentScreen === "outbound") {
      if (selectedNode?.key?.split("-").length === 2) {
        handleNewFieldGroup(true);
        return;
      } else if (
        [
          "CONDITION_IF",
          "CONDITION_ELSE",
          "RECORD",
          "FIELD_GROUP",
          "REPEATING_FIELD_GROUP",
          "RECORD_LOOP",
        ].includes(selectedNode?.ICON_DISPLAY) &&
        openModal 
        // templateHeader?.FILETYPE !== "EDI"
      ) {
        setShowModal(true);
      } else if ((!RECORD_TYPE[selectedNode.ICON_DISPLAY] || (selectedNode.ICON_DISPLAY === 'QUERY' && selectedNode.TYPE === 'QUERY')) && openModal) {
        handleNewFieldGroup(false);
        return;
      } 
    } else {
      if (
        ["FIELD_GROUP", "REPEATING_FIELD_GROUP"].includes(
          selectedNode?.ICON_DISPLAY
        ) &&
        openModal &&
        templateHeader?.FILETYPE !== "EDI"
      ) {
        const isInside = handleFieldGroupPlacement(selectedNode?.key);
        handleNewFieldGroup(isInside);
        return;
      } else if (!RECORD_TYPE[selectedNode.ICON_DISPLAY] && openModal) {
        handleNewFieldGroup(false);
        return;
      } else if (openModal) {
        handleNewFieldGroup(true);
        return;
      }
    }
  }, [openModal]);

  return (
    <Dialog open={showModal} maxWidth="xs" fullWidth>
      <DialogTitle className="dialog-header">
        <div className="title">{t("BOX_CONFIRM")}</div>
      </DialogTitle>
      <DialogContent className="dialog-content" dividers>
        <div ref={contentRef}></div>
      </DialogContent>
      <DialogActions>
        <Button
          size='small'
          variant="outlined"
          color="primary"
          onClick={() => handleNewFieldGroup(true)}>
          {t("BTN_YES")}
        </Button>
        <Button
          size='small'
          variant="outlined"
          color="primary"
          onClick={() => handleNewFieldGroup(false)}>
          {t("BTN_NO")}
        </Button>
        <Button
          size='small'
          variant="outlined"
          color="primary"
          onClick={() => {
            // toggle(null);
            setShowModal(false);
          }}>
          {t("BTN_CANCEL")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default NewFieldGroup;
